import React, { Fragment, useEffect } from 'react';
import useAuth from 'src/hooks/useAuth';
import { TAreas } from 'src/constants/lead/leadTypes';
import { TUserCarType } from 'src/constants/user';
import { channel } from 'src/service/pusher';
import { IStore } from 'src/interfaces/store';
import { getNotificationArea } from 'src/components/Alerts/utils/getNotificationArea';
import {
    ASSIGN_CONVERSATION_ALERT_TYPE,
    INCOMING_CONVERSATION_ALERT_TYPE,
    INCOMING_MESSAGE_ALERT_TYPE,
    CREATED_TICKET_ALERT_TYPE,
    RESPONSE_TICKET_ALERT_TYPE,
    NEW_LEAD_ALERT_TYPE,
} from 'src/components/Alerts/constants';
import { SnackbarAlert } from './SnackbarAlert';
import {
    TNewLeadPayload,
    TNotification,
    TPayload,
    TTicketNotification,
} from 'src/components/Alerts/types';
import {
    DEVELOPER_LEVEL_ACCESS,
    ADMIN_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
    USER_LEVEL_ACCESS,
    TAccessLevel,
} from 'src/components/access';
import { useTranslation } from 'react-i18next';
import { getChannelName } from 'src/utils/format/getChannelName';
import { getVariantColor } from '../utils/getVariantColor';
import useNotification from 'src/hooks/useNotification';
import { formattedBodyForNotificationIncomingMessage } from '../utils/formattedBodyForNotification';

export const WarboxNotifications = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { conversationsAlert, ticketAlert, customAlert } = SnackbarAlert();
    const { updateList } = useNotification();

    const handleShowNotification = (event: TNotification) => {
        const { type, payload } = event;

        switch (type) {
            case INCOMING_CONVERSATION_ALERT_TYPE: {
                const conversationPayload = payload as TPayload;
                const variant = getVariantColor(conversationPayload.channel);
                const channel = getChannelName(conversationPayload.channel);
                const title = `${t('SnackBar.IncomingMessage')} ${channel}`;
                return conversationsAlert(conversationPayload, {
                    variant,
                    title,
                });
            }
            case INCOMING_MESSAGE_ALERT_TYPE: {
                const conversationPayload = payload as TPayload;
                const variant = getVariantColor(conversationPayload.channel);
                const channel = getChannelName(conversationPayload.channel);
                const title = `${t('SnackBar.IncomingMessage')} ${channel}`;
                const notification =
                    formattedBodyForNotificationIncomingMessage({
                        conversationId: conversationPayload?.conversationId,
                        channel,
                    });
                updateList(notification);
                return conversationsAlert(conversationPayload, {
                    variant,
                    title,
                });
            }
            case ASSIGN_CONVERSATION_ALERT_TYPE: {
                const conversationPayload = payload as TPayload;
                const variant = getVariantColor(conversationPayload.channel);
                const channel = getChannelName(conversationPayload.channel);
                const title = `${t('SnackBar.AssignConversation')} ${channel}`;
                return conversationsAlert(conversationPayload, {
                    variant,
                    title,
                });
            }
            case NEW_LEAD_ALERT_TYPE: {
                const newLeadPayload = payload as TNewLeadPayload;
                const notification = {
                    ...newLeadPayload,
                    _id: newLeadPayload._id || newLeadPayload.lead,
                    createdAt: newLeadPayload.createdAt || Date.now(),
                };

                updateList(notification);
                return customAlert(
                    t(newLeadPayload.message),
                    'info',
                    {
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    },
                    `/management/leads/${newLeadPayload.lead}`
                );
            }
            default:
                return;
        }
    };

    const handleShowTicketNotification = (event: TTicketNotification) => {
        const { type, payload } = event;
        const { accessLevel, _id: userId } = user;

        if (
            accessLevel === GROUP_LEVEL_ACCESS &&
            payload.ticketUserId &&
            userId === payload.ticketUserId
        )
            return;

        switch (type) {
            case RESPONSE_TICKET_ALERT_TYPE: {
                const title = `${t('SnackBar.ResponseTicket')}`;
                return ticketAlert(payload, { variant: 'warning', title });
            }
            case CREATED_TICKET_ALERT_TYPE: {
                const title = `${t('SnackBar.TicketCreatedNotification')}`;
                return ticketAlert(payload, { variant: 'warning', title });
            }
            default:
                return;
        }
    };

    const bindByStoreAndArea = (
        storeId: string,
        userArea: TAreas,
        carType: TUserCarType
    ) => {
        const response = getNotificationArea(userArea, carType);
        response.forEach((area) =>
            channel.bind(`${storeId}_${area}`, handleShowNotification)
        );
    };

    const handleNotifications = (accessLevel: TAccessLevel) => {
        const {
            stores = [],
            areas = [],
            carType,
            _id: userId,
            group = {},
            permissions,
        } = user;
        const { _id: groupId } = group || {};

        if (accessLevel === USER_LEVEL_ACCESS) {
            channel.bind(userId, handleShowNotification);
            channel.bind(userId, handleShowTicketNotification);
        }

        if (accessLevel === ADMIN_LEVEL_ACCESS) {
            channel.bind(userId, handleShowTicketNotification);
            stores.forEach((store: IStore) => {
                areas.forEach((userArea: TAreas) => {
                    bindByStoreAndArea(store._id, userArea, carType);
                });
            });
        }

        if (accessLevel === GROUP_LEVEL_ACCESS && groupId) {
            channel.bind(groupId, handleShowTicketNotification);
            channel.bind(groupId, handleShowNotification);
            channel.bind(userId, handleShowTicketNotification);
        }

        if (accessLevel === DEVELOPER_LEVEL_ACCESS) {
            channel.bind(userId, handleShowTicketNotification);
            channel.bind(DEVELOPER_LEVEL_ACCESS, handleShowTicketNotification);
        }
    };

    useEffect(() => {
        if (user?.accessLevel) handleNotifications(user.accessLevel);
    }, [user]);

    return <Fragment />;
};
